import {
  BarChart3,
  Building2,
  CalendarClock,
  CalendarRange,
  ClipboardList,
  FileSpreadsheet,
  FileText,
  Globe,
  House,
  KeyRound,
  Landmark,
  ListChecks,
  Mail,
  PencilRuler,
  PhoneCall,
  RotateCcw,
  ShieldAlert,
  ShieldCheck,
  SlidersHorizontal,
  Table2,
  Tags,
  Trash2,
  TrendingUp,
  Trophy,
  Upload,
  UserPlus,
  Users,
  UsersRound,
  Waypoints,
  type LucideIcon
} from 'lucide-react';

import { DEMO_PATHS, type MockScreen } from '@/demo';
import { TEAM_PATHS } from '@/domains/admin';
import type { Can, Permission } from '@/domains/auth';
import { CONTENT_PATHS } from '@/domains/content';
import { DEMO } from '@/runtime-config';

/**
 * The Team Portal's navigation, grouped by what people do rather than by table names (the
 * two-portals design, §4.1).
 *
 * **A live entry follows its route's guard exactly.** `permission` is what the route's
 * `AdminRoute` names — survey-api's policy for that section's reads (SR-228; the role-model UI
 * design's §4). An entry the account cannot open is not drawn for it at all — the guard would
 * only send it home. Like the guard, this decides what renders; the access control is
 * survey-api's. A mock entry names the permission of the live section it stands beside.
 *
 * **An entry with no screen has no `to`.** It is drawn, disabled and tagged "coming". With the
 * demo on, an entry the demo draws a mock screen for carries it in `mock` — the rail links to it,
 * tagged "mock", and hides it from a role the demo's role matrix does not grant it ("View as
 * role"). With the demo off `mock` is never read, so production shows no invented screen. The
 * workflow each stands for is PRS v1.3's: the compliance queues and My tasks are CMP-09's and the
 * concern criteria CMP-08's (all three live since SR-222, with the scheduled BrokerCheck refresh
 * beside them under Setup), DD calls WF-09, reinstatement WF-08, metrics updates WF-10; rankings
 * (WF-12) are live since SR-231, and the lists, reports and the firm exchange's import (WF-11) since
 * SR-230.
 *
 * This file is the Team Portal's alone. The Advisor Portal has its own (`zones/portal/nav.ts`),
 * and neither names a route of the other: the two sites share no navigation.
 */

export interface ITeamNavEntry {
  label: string;
  icon: LucideIcon;
  /** Absent: no screen yet — drawn disabled, tagged "coming". */
  to?: string;
  /** The demo's mock screen for an entry with no `to`, read only with the demo on. */
  mock?: { to: string; screen: MockScreen };
  /**
   * A live entry whose section still draws invented data on some of its screens — tagged in the
   * rail exactly as the demo's mock entries are.
   *
   * Unlike `mock` it is read with the demo **off** as well, and that is the point: those screens
   * render at a live address in a production deployment, which is the one case where nothing
   * else would say so before somebody opened them.
   */
  invented?: boolean;
  /** Match exactly: Home, whose path every other entry starts with. */
  end?: boolean;
  /** What opens it — the permission its route's `AdminRoute` names. Absent on Home alone, which
   *  every Team Portal account opens, a role-less one included. */
  permission?: Permission;
  /** An area of routed tabs each under a guard of its own ("Users & roles": Users
   *  `accounts.browse`, Roles `roles.manage`), in place of `to` and `permission`: drawn while the
   *  account holds any tab's permission, and leading to the first tab it holds (the role-model UI
   *  design's §4). `teamNavFor` resolves it into a plain entry, so nothing past it reads this
   *  but `lit`. */
  tabs?: readonly { to: string; permission: Permission }[];
  /** Set by `teamNavFor` on a tabbed area: every tab path the account holds, the rail lighting the
   *  entry on any of them — `to` names only the first. */
  lit?: readonly string[];
}

export interface ITeamNavGroup {
  label: string;
  entries: ITeamNavEntry[];
}

export const TEAM_HOME: ITeamNavEntry = {
  label: 'Home',
  icon: House,
  to: TEAM_PATHS.home,
  end: true
};

export const TEAM_NAV: ITeamNavGroup[] = [
  {
    label: 'Work',
    entries: [
      // SR-222's work items (survey-api's blocks 11 and 14), under the participant surface's
      // two policies.
      {
        label: 'My tasks',
        icon: ListChecks,
        to: TEAM_PATHS.tasks,
        permission: 'responses.browse'
      },
      {
        label: 'Compliance queues',
        icon: ShieldAlert,
        to: TEAM_PATHS.compliance,
        permission: 'responses.browse'
      },
      // SR-487's number checks (PFR §4), under the same read; deciding is `responses.annotate`.
      {
        label: 'Number checks',
        icon: ShieldCheck,
        to: TEAM_PATHS.numberChecks,
        permission: 'responses.browse'
      },
      // SR-314: the seeded DD list, live; the interview and the outreach on the record page.
      {
        label: 'DD calls',
        icon: PhoneCall,
        to: TEAM_PATHS.ddCalls,
        permission: 'internal_fields.read'
      },
      // SR-313's reinstatement worklist (WF-08), on Lists' rows under the same read.
      {
        label: 'Reinstatement',
        icon: RotateCcw,
        to: TEAM_PATHS.reinstatement,
        permission: 'responses.browse'
      },
      // SR-315's Metrics updates (RNK-07): who owes an edition's round its figures, and the firms'
      // status — `responses.browse`, the lists' read. Under Work with the other worklists My tasks
      // gathers (SR-485).
      {
        label: 'Metrics updates',
        icon: TrendingUp,
        to: TEAM_PATHS.metricsUpdates,
        permission: 'responses.browse'
      }
    ]
  },
  {
    label: 'Records',
    entries: [
      { label: 'People', icon: Users, to: TEAM_PATHS.people, permission: 'responses.browse' },
      // SR-414's record lists (the owner's Q2, Q4): a team's and a firm's or org's own list and
      // record address, under the participant surface's policy.
      { label: 'Teams', icon: UsersRound, to: TEAM_PATHS.teams, permission: 'responses.browse' },
      { label: 'Firms', icon: Building2, to: TEAM_PATHS.firms, permission: 'responses.browse' },
      // The area's root, not its first tab, so it stays lit on either tab and on a firm's card.
      {
        label: 'SEC ADV register',
        icon: Landmark,
        to: TEAM_PATHS.secAdv,
        permission: 'registry.browse'
      }
    ]
  },
  {
    label: 'Cycle',
    entries: [
      {
        label: 'Responses',
        icon: FileText,
        to: TEAM_PATHS.responses,
        permission: 'responses.browse'
      },
      // SR-470's recycle bin (the owner's Q1 (a)): what was moved out of play lately, across every
      // record — its own entry, since the client's staff do not open Responses. Its route's read.
      {
        label: 'Recycle bin',
        icon: Trash2,
        to: TEAM_PATHS.recycleBin,
        permission: 'responses.browse'
      },
      {
        label: 'Registrations',
        icon: UserPlus,
        to: TEAM_PATHS.registrations,
        permission: 'responses.browse'
      },
      // SR-230's Lists: a survey's saved views as the Conga grid the research team works in —
      // `responses.browse`, the rows' read. It replaced the demo's Worklists mock.
      {
        label: 'Lists',
        icon: Table2,
        to: TEAM_PATHS.lists,
        permission: 'responses.browse'
      },
      // SR-230's Import: a firm's file checked, applied and reversed — `firm_exchange.manage`, every
      // route of it. It replaced the demo's Firm exchange mock.
      {
        label: 'Import',
        icon: Upload,
        to: TEAM_PATHS.imports,
        permission: 'firm_exchange.manage'
      },
      // SR-231's editions and a draft's renumbering, under the policy survey-api reads them by.
      // The demo's mock of the whole build (publish and finalists included) stays reachable from
      // the guide alone, at its own address.
      {
        label: 'Rankings',
        icon: Trophy,
        to: TEAM_PATHS.rankings,
        permission: 'internal_fields.read'
      }
    ]
  },
  {
    label: 'Website',
    entries: [
      // The area's root, not a tab of it, so it stays lit on all seven of its screens and on a
      // post's own address.
      //
      // `content.author` is survey-api's own: it is what every post route asks for, so the entry
      // appears for exactly the people the section answers. Publishing is the separate
      // `content.publish`, which decides a control inside the post editor rather than whether
      // the section is reachable — an author who may write but not publish still works here
      // (WEB-13 AC4).
      //
      // Tagged, because two of its seven screens are still fixtures: events and the site pages.
      // News, the team page, the media library and "Who can publish" read survey-api, and each
      // screen says which it is on itself — the tag is so that is known before one is opened.
      {
        label: 'Website',
        icon: Globe,
        to: CONTENT_PATHS.overview,
        permission: 'content.author',
        invented: true
      }
    ]
  },
  {
    label: 'Insights',
    entries: [
      // SR-230's report views, their preview and kept runs — `responses.browse`, the rows' read.
      {
        label: 'Reports',
        icon: BarChart3,
        to: TEAM_PATHS.reports,
        permission: 'responses.browse'
      }
    ]
  },
  {
    label: 'Setup',
    entries: [
      {
        label: 'Survey builder',
        icon: PencilRuler,
        to: TEAM_PATHS.builder,
        permission: 'definitions.author'
      },
      // SR-226's staff fields. Its own entry rather than a fourth tab of the survey builder:
      // that screen authors what a respondent is asked, and REC-03 exists to keep the two
      // apart — and the builder's model is versioned and frozen on publish, where the
      // catalogue is neither. `internal_fields.author`, reads included.
      {
        label: 'Staff fields',
        icon: Tags,
        to: TEAM_PATHS.staffFields,
        permission: 'internal_fields.author'
      },
      // SR-324: the DD interview templates — read by every reader of staff fields, published by
      // an author of them.
      {
        label: 'DD templates',
        icon: ClipboardList,
        to: TEAM_PATHS.ddTemplates,
        permission: 'internal_fields.read'
      },
      // SR-513: where a TEAMS registration is routed — the Firms table, every firm by CRD on the
      // wirehouse list (Wires) or off it (Gating), with its names from the team firm register.
      // Reference data, not records: read under `responses.browse`, changed under
      // `responses.annotate`.
      {
        label: 'Team routing',
        icon: Waypoints,
        to: TEAM_PATHS.teamRouting,
        permission: 'responses.browse'
      },
      { label: 'Cycles', icon: CalendarRange, to: TEAM_PATHS.cycles, permission: 'cycles.manage' },
      // Two routed tabs, each a section of its own (SR-228): Users `accounts.browse`, Roles
      // `roles.manage` — the entry drawn while either is held, lit on both.
      {
        label: 'Users & roles',
        icon: KeyRound,
        tabs: [
          { to: TEAM_PATHS.users, permission: 'accounts.browse' },
          { to: TEAM_PATHS.roles, permission: 'roles.manage' }
        ]
      },
      {
        label: 'Concern criteria',
        icon: SlidersHorizontal,
        to: TEAM_PATHS.criteria,
        permission: 'compliance.configure'
      },
      // SR-222's scheduled BrokerCheck refresh: every route `integrations.configure` (SR-228's
      // split from `compliance.configure`), its reads included.
      // The area's root, its Schedule tab, so it stays lit on the Runs tab and on a run.
      {
        label: 'BrokerCheck refresh',
        icon: CalendarClock,
        to: TEAM_PATHS.refresh,
        permission: 'integrations.configure'
      },
      // SR-230's export layouts: the files Veritant sends a firm and reads back. `firm_exchange.manage`,
      // its reads included — configured here, read by Lists' export and Import.
      {
        label: 'Export layouts',
        icon: FileSpreadsheet,
        to: TEAM_PATHS.exportLayouts,
        permission: 'firm_exchange.manage'
      },
      {
        label: 'Templates',
        icon: Mail,
        permission: 'roles.manage',
        mock: { to: DEMO_PATHS.templates, screen: 'templates' }
      }
    ]
  }
];

/** `entry` as the account `can` describes is shown it — a tabbed area resolved to the first tab
 *  it holds — or `null` where the account opens none of it. */
function resolve(entry: ITeamNavEntry, can: Can): Nullable<ITeamNavEntry> {
  if (entry.tabs) {
    const held = entry.tabs.filter((candidate) => can(candidate.permission));
    const [tab] = held;
    return tab
      ? {
          ...entry,
          to: tab.to,
          permission: tab.permission,
          tabs: undefined,
          lit: held.map((candidate) => candidate.to)
        }
      : null;
  }
  return entry.permission === undefined || can(entry.permission) ? entry : null;
}

/** The groups as the account is shown them: the entries it opens only, each leading where it
 *  opens, and no group left empty — so Setup is drawn while any entry in it is. */
export function teamNavFor(can: Can): ITeamNavGroup[] {
  return TEAM_NAV.map((group) => ({
    ...group,
    entries: group.entries.flatMap((entry) => resolve(entry, can) ?? [])
  })).filter((group) => group.entries.length > 0);
}

/** Where an entry leads: its screen; with the demo on, the demo's mock of it; or nowhere yet. */
export function entryTarget(entry: ITeamNavEntry): Nullable<{ to: string; mock: boolean }> {
  if (entry.to) return { to: entry.to, mock: false };
  if (DEMO && entry.mock) return { to: entry.mock.to, mock: true };
  return null;
}

/** Every live entry the account opens, Home first — what the command palette offers as
 *  screens. */
export function liveScreensFor(can: Can): (ITeamNavEntry & { to: string })[] {
  return [TEAM_HOME, ...teamNavFor(can).flatMap((group) => group.entries)].filter(
    (entry): entry is ITeamNavEntry & { to: string } => entry.to !== undefined
  );
}
